import { useState } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Employee } from '@lms/shared-types'
import { PageHead } from '../components/primitives/PageHead'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { Button } from '../components/primitives/Button'
import { Card } from '../components/primitives/Card'
import { Field } from '../components/primitives/Field'
import { useToast } from '../components/primitives/Toast'
import { EmployeePickerInput } from '../components/primitives/EmployeePickerInput'
import { DateField } from '../components/primitives/DateField'
import {
  createApproverOverride,
  listApproverOverrides,
  revokeApproverOverride,
} from '../api/approver-overrides'
import { fmtDate } from '../lib/format'

const OVERRIDES_KEY = ['approver-overrides', 'active'] as const

type CoverTab = 'create' | 'active'

const TABS: ReadonlyArray<{ id: CoverTab; label: string }> = [
  { id: 'create', label: 'Approver cover' },
  { id: 'active', label: 'Active approver covers' },
]

/**
 * HR "Approver cover" surface (/hr/approver-cover).
 *
 * HR temporarily reroutes an approver's incoming leave requests to a stand-in
 * for a date window. New requests filed in the window route to the stand-in
 * (bind-at-submit); requests already filed are unaffected. Migration 0175.
 *
 * Two tabs: "Approver cover" (create) and "Active approver covers" (list +
 * revoke). Tab selection is reflected in the URL as ?tab=create|active.
 */
export function HrApproverCoverPage(): JSX.Element {
  return (
    <HrAccessGuard>
      <div className="page">
        <HrApproverCoverPageInner />
      </div>
    </HrAccessGuard>
  )
}

function HrApproverCoverPageInner(): JSX.Element {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, string | undefined>
  const rawTab = search['tab']
  const activeTab: CoverTab = rawTab === 'active' ? 'active' : 'create'

  function handleTabChange(tab: CoverTab): void {
    void navigate({ to: '/hr/approver-cover', search: { tab }, replace: true })
  }

  return (
    <>
      <PageHead
        eyebrow="HR"
        title="Approver cover"
        subtitle="Temporarily reroute an approver's incoming leave requests to a stand-in for a date range."
      />

      <div role="tablist" aria-label="Approver cover tabs" style={tabListStyle} data-testid="cover-tab-list">
        {TABS.map((tab, i) => {
          const isActive = tab.id === activeTab
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`cover-tabpanel-${tab.id}`}
              id={`cover-tab-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={`cover-tab-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
                e.preventDefault()
                const delta = e.key === 'ArrowRight' ? 1 : -1
                const next = TABS[(i + delta + TABS.length) % TABS.length]
                if (next) handleTabChange(next.id)
              }}
              style={{ ...tabButtonStyle, ...(isActive ? tabButtonActiveStyle : {}) }}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <div
        role="tabpanel"
        id={`cover-tabpanel-${activeTab}`}
        aria-labelledby={`cover-tab-${activeTab}`}
        data-testid={`cover-tabpanel-${activeTab}`}
        style={panelStyle}
      >
        {activeTab === 'create' ? (
          <CreateCoverForm onCreated={() => handleTabChange('active')} />
        ) : (
          <ActiveCoversList />
        )}
      </div>
    </>
  )
}

// ── Create tab ────────────────────────────────────────────────────────────────

function CreateCoverForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()

  const [covered, setCovered] = useState<Employee | null>(null)
  const [cover, setCover] = useState<Employee | null>(null)
  const [startsOn, setStartsOn] = useState<string>('')
  const [endsOn, setEndsOn] = useState<string>('')

  const validationError: string | null = (() => {
    if (!covered) return 'Pick the approver going away (being covered).'
    if (!cover) return 'Pick the stand-in who will receive their requests.'
    if (covered.id === cover.id) return 'Covered approver and stand-in must differ.'
    if (!startsOn) return 'Pick a start date.'
    if (!endsOn) return 'Pick an end date.'
    if (endsOn < startsOn) return 'End date must be on or after the start date.'
    return null
  })()

  const createMutation = useMutation({
    mutationFn: () =>
      createApproverOverride({
        coveredApproverId: covered!.id,
        coverApproverId: cover!.id,
        startsOn,
        endsOn,
      }),
    onSuccess: () => {
      notify('Approver cover created', 'success')
      setCovered(null)
      setCover(null)
      setStartsOn('')
      setEndsOn('')
      void queryClient.invalidateQueries({ queryKey: OVERRIDES_KEY })
      onCreated()
    },
    onError: (err: unknown) => {
      notify(err instanceof Error ? err.message : 'Could not create cover', 'error')
    },
  })

  return (
    <Card padding={20} style={cardStyle}>
      <Field label="Approver being covered" required>
        <EmployeePickerInput
          value={covered}
          onChange={setCovered}
          testId="covered-picker"
          placeholder="Search the approver going on leave…"
          {...(cover ? { excludeId: cover.id } : {})}
          clearLabel="Change"
        />
      </Field>
      <Field label="Stand-in approver" required>
        <EmployeePickerInput
          value={cover}
          onChange={setCover}
          testId="cover-picker"
          placeholder="Search the stand-in who will decide requests…"
          {...(covered ? { excludeId: covered.id } : {})}
          clearLabel="Change"
        />
      </Field>
      <Field label="Starts on" required>
        <DateField
          value={startsOn || null}
          onChange={(iso) => setStartsOn(iso ?? '')}
          placeholder="Pick a start date"
          data-testid="starts-on"
          aria-label="Starts on"
        />
      </Field>
      <Field label="Ends on" required>
        <DateField
          value={endsOn || null}
          onChange={(iso) => setEndsOn(iso ?? '')}
          {...(startsOn ? { min: startsOn } : {})}
          placeholder="Pick an end date"
          data-testid="ends-on"
          aria-label="Ends on"
        />
      </Field>
      {validationError ? <p style={hintStyle}>{validationError}</p> : null}
      <Button
        variant="primary"
        disabled={validationError !== null || createMutation.isPending}
        onClick={() => createMutation.mutate()}
        data-testid="create-override"
      >
        {createMutation.isPending ? 'Creating…' : 'Create cover'}
      </Button>
    </Card>
  )
}

// ── Active tab ─────────────────────────────────────────────────────────────────

function ActiveCoversList(): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()

  const overrides = useQuery({
    queryKey: OVERRIDES_KEY,
    queryFn: () => listApproverOverrides({ activeOnly: true }),
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApproverOverride(id),
    onSuccess: () => {
      notify('Cover revoked', 'success')
      void queryClient.invalidateQueries({ queryKey: OVERRIDES_KEY })
    },
    onError: (err: unknown) => {
      notify(err instanceof Error ? err.message : 'Could not revoke cover', 'error')
    },
  })

  const rows = overrides.data ?? []

  return (
    <Card padding={20} style={cardStyle}>
      {overrides.isLoading ? (
        <p style={hintStyle}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={hintStyle}>No active approver covers. Use the &ldquo;Approver cover&rdquo; tab to create one.</p>
      ) : (
        <ul style={listStyle} data-testid="overrides-list">
          {rows.map((o, i) => (
            <li
              key={o.id}
              style={{
                ...rowStyle,
                ...(i < rows.length - 1 ? rowDividerStyle : {}),
              }}
            >
              <div>
                <div style={nameStyle}>
                  {o.coveredApproverName} → {o.coverApproverName}
                </div>
                <div style={dateStyle}>
                  {fmtDate(o.startsOn)} – {fmtDate(o.endsOn)}
                </div>
              </div>
              <Button
                variant="tertiary"
                size="sm"
                disabled={revokeMutation.isPending}
                onClick={() => revokeMutation.mutate(o.id)}
                data-testid={`revoke-${o.id}`}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

// Matches the HR hub tabs (pages/hr-hub/TabbedHub) used by /hr/import-export:
// no baseline rule, active = steel-blue colour + weight + a 2px underline.
const tabListStyle: CSSProperties = {
  display: 'flex',
  gap: 18,
  marginBottom: 20,
  flexWrap: 'wrap',
}
const tabButtonStyle: CSSProperties = {
  padding: '4px 0 6px',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  outline: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  color: 'var(--text-3)',
  transition: 'color 120ms ease, border-color 120ms ease',
}
const tabButtonActiveStyle: CSSProperties = {
  color: 'var(--steel-blue)',
  fontWeight: 700,
  borderBottom: '2px solid var(--steel-blue)',
}
// Match the Coverage hub (pages/hr-coverage): both the form and the history
// list sit in a 720px column.
const panelStyle: CSSProperties = { maxWidth: 720 }
const cardStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16 }
const hintStyle: CSSProperties = { margin: 0, fontSize: 13, color: 'var(--text-2, #71717a)' }
const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
}
const rowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
}
const rowDividerStyle: CSSProperties = {
  borderBottom: '1px solid var(--border, #e4e4e7)',
}
const nameStyle: CSSProperties = { fontSize: 14, fontWeight: 600, color: 'var(--text)' }
const dateStyle: CSSProperties = { fontSize: 12, color: 'var(--text-2, #71717a)', marginTop: 2 }
